import { useState, useMemo, useEffect } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CategoryWithMonthData } from '../types/category';
import { useUserPreferences } from '../contexts/UserPreferencesContext';

interface CategorySelectorProps {
  categories: CategoryWithMonthData[];
  selectedCategoryIds: string[];
  onToggleCategory: (categoryId: string) => void;
  onBatchToggle?: (categoryIds: string[], shouldSelect: boolean) => void;
  onConfirmSelection?: (categoryIds: string[]) => void; // Called when user clicks Done with final selection
  loading?: boolean;
  type: 'expense' | 'transfer';
  hideLabel?: boolean; // For table context where header provides label
  compact?: boolean; // Smaller height for table rows
  remainingAllocation?: number; // For showing remaining balance in compact mode
  formatMoney?: (value: number) => string; // For formatting the remaining allocation
}

interface FlatCategory {
  id: string;
  name: string;
  parentId: string;
  parentName: string;
  balance?: number | null;
}

export default function CategorySelector({
  categories,
  selectedCategoryIds,
  onToggleCategory,
  onBatchToggle,
  onConfirmSelection,
  loading = false,
  type,
  hideLabel = false,
  compact = false,
  remainingAllocation,
  formatMoney: formatMoneyProp
}: CategorySelectorProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingSelections, setPendingSelections] = useState<string[]>([]);
  const { t } = useTranslation();
  const { formatMoney } = useUserPreferences();

  // Initialize pending selections when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setPendingSelections([...selectedCategoryIds]);
    }
  }, [isModalOpen, selectedCategoryIds]);

  // Toggle a category in pending selections
  const handlePendingToggle = (categoryId: string) => {
    setPendingSelections(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Toggle all categories in a group
  const handlePendingBatchToggle = (categoryIds: string[], shouldSelect: boolean) => {
    setPendingSelections(prev => {
      if (shouldSelect) {
        // Add all that aren't already selected
        const newIds = categoryIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      } else {
        // Remove all in the batch
        return prev.filter(id => !categoryIds.includes(id));
      }
    });
  };

  // Confirm selection and close modal
  const handleConfirm = () => {
    // Call appropriate callbacks based on what changed
    if (onConfirmSelection) {
      onConfirmSelection(pendingSelections);
    } else if (onBatchToggle) {
      // Clear previous selections and add new ones
      const toRemove = selectedCategoryIds.filter(id => !pendingSelections.includes(id));
      const toAdd = pendingSelections.filter(id => !selectedCategoryIds.includes(id));
      
      if (toRemove.length > 0) {
        onBatchToggle(toRemove, false);
      }
      if (toAdd.length > 0) {
        onBatchToggle(toAdd, true);
      }
    } else {
      // Fallback to individual toggles
      const toRemove = selectedCategoryIds.filter(id => !pendingSelections.includes(id));
      const toAdd = pendingSelections.filter(id => !selectedCategoryIds.includes(id));
      
      toRemove.forEach(id => onToggleCategory(id));
      toAdd.forEach(id => onToggleCategory(id));
    }
    
    setIsModalOpen(false);
    setSearchQuery('');
  };

  // Flatten categories with parent info
  const flatCategories: FlatCategory[] = useMemo(() => {
    return categories.flatMap(group =>
      (group.children || []).map(cat => ({
        id: cat.id,
        name: cat.name,
        parentId: group.id,
        parentName: group.name,
        balance: cat.balance
      }))
    );
  }, [categories]);

  // Filter categories based on search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) {
      return categories;
    }

    const query = searchQuery.toLowerCase();
    return categories
      .map(group => {
        const matchingChildren = (group.children || []).filter(cat =>
          cat.name.toLowerCase().includes(query) ||
          group.name.toLowerCase().includes(query)
        );

        if (matchingChildren.length > 0) {
          return { ...group, children: matchingChildren };
        }
        return null;
      })
      .filter(Boolean) as CategoryWithMonthData[];
  }, [categories, searchQuery]);

  // Get selected categories with details
  const selectedCategories = useMemo(() => {
    return selectedCategoryIds
      .map(id => flatCategories.find(cat => cat.id === id))
      .filter(Boolean) as FlatCategory[];
  }, [selectedCategoryIds, flatCategories]);

  // Check if all children in a group are selected (in pending selections)
  const isGroupFullySelected = (group: CategoryWithMonthData) => {
    const childIds = (group.children || []).map(cat => cat.id);
    return childIds.length > 0 && childIds.every(id => pendingSelections.includes(id));
  };

  // Check if some (but not all) children in a group are selected (in pending selections)
  const isGroupPartiallySelected = (group: CategoryWithMonthData) => {
    const childIds = (group.children || []).map(cat => cat.id);
    const selectedCount = childIds.filter(id => pendingSelections.includes(id)).length;
    return selectedCount > 0 && selectedCount < childIds.length;
  };

  // Get pending selected categories with details (for footer display)
  const pendingSelectedCategories = useMemo(() => {
    return pendingSelections
      .map(id => flatCategories.find(cat => cat.id === id))
      .filter(Boolean) as FlatCategory[];
  }, [pendingSelections, flatCategories]);
  if (loading) {
    return (
      <div className="text-sm text-[#077f5d] font-mono">{t('category_selector.loading')}</div>
    );
  }

  return (
    <div>
      {!hideLabel && (
        <label
          className="block text-[#077f5d] mb-2 uppercase"
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: '10px',
            fontWeight: 700
          }}
        >
          {t('category_selector.label')} {type === 'transfer' && <span className="text-xs">({t('category_selector.optional')})</span>}
        </label>
      )}
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className={`w-full flex items-center justify-between bg-white border-2 border-[#077f5d] hover:bg-[#dffbd0] font-mono focus:border-[#3cbe8c] ${compact ? 'px-2 py-1.5 text-xs' : 'px-4 py-3'}`}
        style={{
          boxShadow: compact ? '2px 2px 0 #077f5d' : '4px 4px 0 #077f5d',
          transition: 'none'
        }}
      >
        <div className="flex-1 text-left min-w-0">
          {selectedCategories.length > 0 ? (
            <>
              <span className="text-[#1a1a1a] block truncate">
                {selectedCategories[0].parentName} - {selectedCategories[0].name}
              </span>
              {compact && remainingAllocation !== undefined && formatMoneyProp && (
                <span className="text-[9px] text-[#077f5d] block">
                  {t('transaction.remaining')}: {formatMoneyProp(remainingAllocation)}
                </span>
              )}
            </>
          ) : (
            <span className="text-[#077f5d]">
              {type === 'transfer' 
                ? `${t('transaction.category')} (${t('transaction.optional')})`
                : t('transaction.category')
              }
            </span>
          )}
        </div>
        <ChevronDown className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-[#077f5d] flex-shrink-0 ml-1`} />
      </button>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="bg-white border-4 border-[#077f5d] max-w-2xl w-full max-h-[80vh] flex flex-col"
            style={{ boxShadow: '6px 6px 0 #077f5d' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-3 border-b-4 border-[#077f5d] bg-[#3cbe8c]">
              <h3
                className="text-white uppercase"
                style={{
                  fontFamily: "'Press Start 2P', monospace",
                  fontSize: '12px',
                  fontWeight: 700
                }}
              >
                {t('category_selector.select_category')} {type === 'transfer' && <span className="text-xs">({t('category_selector.optional')})</span>}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-white border-2 border-[#077f5d]"
                style={{
                  boxShadow: '2px 2px 0 #077f5d',
                  transition: 'none'
                }}
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Modal Content */}
            <div
              className="flex-1 overflow-y-auto p-5 bg-[#ffffeb]"
              style={{
                backgroundImage: `
                  repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
                  repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
                `
              }}
            >
              {/* Search Bar */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#077f5d]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('category_selector.search_placeholder')}
                  className="w-full pl-10 pr-10 py-2.5 bg-white border-2 border-[#077f5d] outline-none text-sm font-mono"
                  style={{
                    boxShadow: '2px 2px 0 #077f5d',
                    transition: 'none'
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#077f5d] hover:text-[#1a1a1a]"
                    style={{ transition: 'none' }}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Category List */}
              <div className="space-y-3">
                {filteredGroups.length === 0 ? (
                  <div className="text-center py-12 text-[#077f5d] text-sm font-mono">
                    {t('category_selector.no_match')}
                  </div>
                ) : (
                  filteredGroups.map((group) => {
                    const childIds = (group.children || []).map(cat => cat.id);
                    const groupFullySelected = isGroupFullySelected(group);
                    const groupPartiallySelected = isGroupPartiallySelected(group);
                    
                    return (
                      <div key={group.id} className="space-y-2">
                        {/* Group Header with checkbox to select all */}
                        <label 
                          className="flex items-center gap-3 font-bold text-sm text-white px-3 py-2 bg-[#3cbe8c] border-2 border-[#077f5d] sticky top-0 font-mono uppercase cursor-pointer hover:bg-[#2da877]" 
                          style={{ boxShadow: '2px 2px 0 #077f5d', transition: 'none' }}
                        >
                          <input
                            type="checkbox"
                            checked={groupFullySelected}
                            ref={(el) => {
                              if (el) el.indeterminate = groupPartiallySelected;
                            }}
                            onChange={() => handlePendingBatchToggle(childIds, !groupFullySelected)}
                            className="w-4 h-4 border-2 border-white cursor-pointer accent-white"
                          />
                          {group.name}
                        </label>

                        {/* Child Categories */}
                        <div className="space-y-1.5 pl-2">
                          {(group.children || []).map((cat) => {
                            const isSelected = pendingSelections.includes(cat.id);
                            return (
                              <label
                                key={cat.id}
                                className={`flex items-center gap-3 p-3 cursor-pointer border-2 ${isSelected
                                    ? type === 'expense'
                                      ? 'bg-[#dffbd0] border-[#077f5d]'
                                      : 'bg-blue-50 border-blue-700'
                                    : 'bg-white border-[#077f5d] hover:bg-[#dffbd0]'
                                  }`}
                                style={{
                                  boxShadow: '2px 2px 0 #077f5d',
                                  transition: 'none'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handlePendingToggle(cat.id)}
                                  className={`w-4 h-4 border-2 border-[#077f5d] cursor-pointer ${type === 'expense'
                                      ? 'text-[#3cbe8c] focus:ring-[#077f5d]'
                                      : 'text-blue-500 focus:ring-[#077f5d]'
                                    }`}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="font-bold text-sm text-[#1a1a1a] font-mono">{cat.name}</div>
                                  <div className="text-xs text-[#077f5d] font-mono">
                                    {cat.is_goal ? (
                                      // Goal category: show remaining amount to reach target
                                      cat.goal_monthly_target ? (
                                        // WITH target month: monthly target - current month transactions
                                        <>{t('category_selector.remaining')}: {formatMoney(Math.max(0, cat.goal_monthly_target - (cat.transaction || 0)))}</>
                                      ) : (
                                        // WITHOUT target month: total target - accumulated saved
                                        <>{t('category_selector.remaining')}: {formatMoney(Math.max(0, (cat.goal_target || 0) - (cat.goal_saved || 0)))}</>
                                      )
                                    ) : (
                                      // Non-goal: show balance
                                      <>{t('category_selector.remaining')}: {formatMoney(cat.balance || 0)}</>
                                    )}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t-2 border-[#077f5d] bg-white">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-[#077f5d] font-mono">
                  {pendingSelectedCategories.length === 0
                    ? t('category_selector.none_selected')
                    : t('category_selector.categories_selected', { count: pendingSelectedCategories.length })}
                </span>
              </div>
              <button
                onClick={handleConfirm}
                className={`w-full py-3 font-bold text-white border-2 uppercase font-mono ${type === 'expense'
                    ? 'bg-[#3cbe8c] border-[#077f5d] hover:bg-[#2da877]'
                    : 'bg-blue-500 border-blue-700 hover:bg-blue-600'
                  }`}
                style={{
                  boxShadow: type === 'expense' ? '3px 3px 0 #077f5d' : '3px 3px 0 #1e40af',
                  transition: 'none'
                }}
              >
                {t('category_selector.done')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
